import { normalizeSiteUrl } from "./site-url"

export const landingTitle =
  "AI Video Clipper: Turn Long Videos into Shorts | intoshort"

export const landingDescription =
  "An AI video clipper for YouTube videos, Twitch VODs and uploads. Turn long videos into vertical shorts with captions, then edit the cuts and export."

/** Public HTTPS origin used for canonical URLs and social metadata. */
export const siteUrl = normalizeSiteUrl(import.meta.env.VITE_SITE_URL)

export const landingMeta = [
  { title: landingTitle },
  { name: "description", content: landingDescription },
  {
    name: "robots",
    content:
      siteUrl && import.meta.env.PROD ? "index,follow" : "noindex,follow",
  },
  { property: "og:site_name", content: "intoshort" },
  { property: "og:locale", content: "en_US" },
  { property: "og:type", content: "website" },
  { property: "og:title", content: landingTitle },
  { property: "og:description", content: landingDescription },
  ...(siteUrl
    ? [
        { property: "og:url", content: `${siteUrl}/` },
        { property: "og:image", content: `${siteUrl}/og-image.png` },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        {
          property: "og:image:alt",
          content:
            "intoshort turns one long video into short-form drafts you can review and edit.",
        },
      ]
    : []),
  { name: "twitter:card", content: "summary_large_image" },
  { name: "twitter:title", content: landingTitle },
  { name: "twitter:description", content: landingDescription },
  ...(siteUrl
    ? [{ name: "twitter:image", content: `${siteUrl}/og-image.png` }]
    : []),
]

export const landingLinks = [
  ...(siteUrl ? [{ rel: "canonical", href: `${siteUrl}/` }] : []),
]

export function landingStructuredData() {
  return siteUrl
    ? {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "intoshort",
        alternateName: "Into Short",
        description: landingDescription,
        url: siteUrl,
        inLanguage: "en-US",
      }
    : null
}

export function publicPageHead({
  path,
  title,
  description,
  article,
}: {
  path: `/${string}`
  title: string
  description: string
  article?: { publishedAt: string }
}) {
  const url = siteUrl ? `${siteUrl}${path}` : null
  return {
    meta: [
      { title },
      { name: "description", content: description },
      {
        name: "robots",
        content:
          siteUrl && import.meta.env.PROD ? "index,follow" : "noindex,follow",
      },
      { property: "og:site_name", content: "intoshort" },
      { property: "og:locale", content: "en_US" },
      { property: "og:type", content: article ? "article" : "website" },
      ...(article
        ? [{ property: "article:published_time", content: article.publishedAt }]
        : []),
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      ...(url ? [{ property: "og:url", content: url }] : []),
      ...(siteUrl
        ? [{ property: "og:image", content: `${siteUrl}/og-image.png` }]
        : []),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: url ? [{ rel: "canonical", href: url }] : [],
    scripts: url
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": article ? "BlogPosting" : "WebPage",
              ...(article
                ? {
                    headline: title,
                    datePublished: article.publishedAt,
                    image: `${siteUrl}/og-image.png`,
                    author: {
                      "@type": "Organization",
                      name: "intoshort",
                      url: siteUrl,
                    },
                    mainEntityOfPage: url,
                  }
                : {}),
              name: title,
              description,
              url,
              inLanguage: "en-US",
              isPartOf: {
                "@type": "WebSite",
                name: "intoshort",
                url: siteUrl,
              },
            }),
          },
        ]
      : [],
  }
}
